/**
 * AuthMarketingPanel — premium dark marketing panel for the auth pages (sign-up / login).
 * Wide (~60%) on large screens, hidden below lg.
 *
 * Premium aesthetic: layered aurora gradient-mesh + masked dot-grid + a living AI network
 * constellation (SVG neural graph: micro-star field, organic node mesh, energy pulses travelling
 * along connections, a breathing core with rotating orbital ring + corona, drifting space dust)
 * set over a blurred purple nebula, with two-layer mouse parallax and a cursor-follow spotlight.
 * Conveys an always-on, connected assistant. Copy passed per-page so sign-up & login share one
 * treatment.
 */
import React, { useRef, useCallback, useMemo } from 'react'
import logoIcon from '@/assets/logo-wn-sc.png'

interface AuthMarketingPanelProps {
  heading: string
  subtext: string
  bullets: string[]
}

/* ── AI network constellation ─────────────────────────────────────────────────
   A deliberately organic (non-symmetric) neural graph that fills the panel's blank right column.
   Depth comes from tiered node sizes + opacity, a deep micro-star field, drifting space dust and a
   blurred nebula behind the core. Energy pulses travel along the connections; the core breathes and
   is ringed by a slow orbital + corona. The parent layers apply two-depth mouse parallax. */
const VB_W = 440
const VB_H = 760

type Tier = 'core' | 'hub' | 'near' | 'mid' | 'far'
type NodeDef = { x: number; y: number; tier: Tier }

const NODE_R: Record<Tier, number> = { core: 5.5, hub: 5, near: 3.4, mid: 3, far: 2.2 }
const NODE_OPACITY: Record<Tier, number> = { core: 1, hub: 1, near: 0.85, mid: 0.6, far: 0.35 }
const NODE_GLOW: Record<Tier, number> = { core: 3, hub: 3, near: 2, mid: 1.5, far: 1 }

// Deterministic PRNG so the star/dust fields are stable across renders (no re-randomising flicker).
function makeRng(seed: number) {
  let s = seed >>> 0
  return () => {
    s = (s + 0x6d2b79f5) >>> 0
    let t = s
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}
const rnd = (rng: () => number, min: number, max: number) => min + rng() * (max - min)

function NetworkConstellation() {
  const NODES: Record<string, NodeDef> = {
    core: { x: 240, y: 372, tier: 'core' },
    a: { x: 352, y: 236, tier: 'hub' },
    b: { x: 150, y: 548, tier: 'hub' },
    c: { x: 330, y: 452, tier: 'near' },
    d: { x: 150, y: 300, tier: 'near' },
    e: { x: 256, y: 318, tier: 'near' },
    f: { x: 410, y: 150, tier: 'mid' },
    g: { x: 98, y: 194, tier: 'mid' },
    h: { x: 388, y: 560, tier: 'mid' },
    i: { x: 268, y: 618, tier: 'mid' },
    j: { x: 196, y: 128, tier: 'far' },
    k: { x: 74, y: 420, tier: 'far' },
    l: { x: 424, y: 372, tier: 'far' },
    m: { x: 300, y: 690, tier: 'far' },
    o1: { x: 60, y: 636, tier: 'far' }, // faint disconnected particles
    o2: { x: 416, y: 690, tier: 'far' },
  }
  const LINES: Array<[string, string]> = [
    ['core', 'a'],
    ['core', 'b'],
    ['core', 'c'],
    ['core', 'd'],
    ['core', 'e'],
    ['a', 'f'],
    ['a', 'e'],
    ['a', 'l'],
    ['b', 'i'],
    ['b', 'k'],
    ['c', 'h'],
    ['c', 'l'],
    ['d', 'g'],
    ['d', 'k'],
    ['e', 'j'],
    ['g', 'j'],
    ['i', 'm'],
    ['h', 'm'],
  ]
  // Energy pulses on a curated subset — varied durations + staggered (negative) starts.
  const PULSES: Array<{ line: [string, string]; dur: number; delay: number }> = [
    { line: ['core', 'a'], dur: 8, delay: 0 },
    { line: ['core', 'b'], dur: 10, delay: -3 },
    { line: ['core', 'c'], dur: 7, delay: -1.5 },
    { line: ['core', 'd'], dur: 9, delay: -4 },
    { line: ['a', 'f'], dur: 8, delay: -2 },
    { line: ['b', 'i'], dur: 10, delay: -5 },
    { line: ['c', 'h'], dur: 9, delay: -6 },
    { line: ['e', 'j'], dur: 7, delay: -2.5 },
  ]

  const stars = useMemo(() => {
    const rng = makeRng(1337)
    return Array.from({ length: 34 }, () => ({
      x: rnd(rng, 8, VB_W - 8),
      y: rnd(rng, 8, VB_H - 8),
      r: rnd(rng, 0.5, 1.5),
      o: rnd(rng, 0.06, 0.16),
      dur: rnd(rng, 3, 6),
      delay: rnd(rng, 0, 6),
    }))
  }, [])
  const dust = useMemo(() => {
    const rng = makeRng(9001)
    return Array.from({ length: 11 }, () => ({
      x: rnd(rng, 24, VB_W - 24),
      y: rnd(rng, 60, VB_H - 60),
      r: rnd(rng, 0.6, 1.8),
      dur: rnd(rng, 20, 40),
      delay: rnd(rng, -20, 0),
      drift: rnd(rng, -12, -22),
    }))
  }, [])

  const core = NODES.core

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="h-full w-auto"
      style={{ overflow: 'visible' }}
      aria-hidden="true"
    >
      <defs>
        <filter id="nc-glow-strong" x="-200%" y="-200%" width="500%" height="500%">
          <feGaussianBlur stdDeviation="3.2" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="b" />
          </feMerge>
        </filter>
        <filter id="nc-glow-soft" x="-200%" y="-200%" width="500%" height="500%">
          <feGaussianBlur stdDeviation="1.6" />
        </filter>
        <radialGradient id="nc-core-grad" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
          <stop offset="35%" stopColor="#cabfff" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#8b7bf0" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="nc-halo-grad" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#7b6ae8" stopOpacity="0.28" />
          <stop offset="100%" stopColor="#7b6ae8" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* 1 · deep micro-star field */}
      {stars.map((s, i) => (
        <circle
          key={`s${i}`}
          className="nc-star"
          cx={s.x}
          cy={s.y}
          r={s.r}
          style={
            { '--o': s.o, '--dur': `${s.dur}s`, animationDelay: `${s.delay}s` } as React.CSSProperties
          }
        />
      ))}

      {/* 2 · connections — very thin, faintly breathing */}
      {LINES.map(([from, to], i) => {
        const A = NODES[from]
        const B = NODES[to]
        return (
          <line
            key={`${from}-${to}`}
            className="nc-wire"
            x1={A.x}
            y1={A.y}
            x2={B.x}
            y2={B.y}
            style={{ animationDelay: `${(i % 5) * -1.1}s` }}
          />
        )
      })}

      {/* 3 · energy pulses travelling along a subset of connections */}
      {PULSES.map((p, idx) => {
        const A = NODES[p.line[0]]
        const B = NODES[p.line[1]]
        const path = `M ${A.x},${A.y} L ${B.x},${B.y}`
        return (
          <circle key={`pulse${idx}`} r={2} fill="#eae6ff" filter="url(#nc-glow-soft)">
            <animateMotion
              dur={`${p.dur}s`}
              begin={`${p.delay}s`}
              repeatCount="indefinite"
              path={path}
              calcMode="linear"
            />
            <animate
              attributeName="opacity"
              dur={`${p.dur}s`}
              begin={`${p.delay}s`}
              repeatCount="indefinite"
              values="0;1;1;0"
              keyTimes="0;0.18;0.82;1"
            />
          </circle>
        )
      })}

      {/* 4 · drifting space dust */}
      {dust.map((d, i) => (
        <circle
          key={`d${i}`}
          className="nc-dust"
          cx={d.x}
          cy={d.y}
          r={d.r}
          style={
            {
              '--drift': `${d.drift}px`,
              animationDuration: `${d.dur}s`,
              animationDelay: `${d.delay}s`,
            } as React.CSSProperties
          }
        />
      ))}

      {/* 5 · satellite nodes (depth via size + opacity + glow) */}
      {Object.entries(NODES)
        .filter(([id]) => id !== 'core')
        .map(([id, n], i) => {
          const strong = n.tier === 'hub'
          return (
            <g key={id} style={{ opacity: NODE_OPACITY[n.tier] }}>
              <circle
                className="nc-node"
                cx={n.x}
                cy={n.y}
                r={NODE_R[n.tier] + NODE_GLOW[n.tier]}
                filter={strong ? 'url(#nc-glow-strong)' : 'url(#nc-glow-soft)'}
                style={{ animationDelay: `${i * 0.37}s` }}
              />
              <circle className="nc-node-dot" cx={n.x} cy={n.y} r={NODE_R[n.tier]} />
            </g>
          )
        })}

      {/* 6 · core cluster — halo, rotating corona + orbital ring, radar pulses, breathing core */}
      <g>
        <circle className="nc-halo" cx={core.x} cy={core.y} r={70} fill="url(#nc-halo-grad)" />

        {/* rotating faint corona (dashed) */}
        <circle
          cx={core.x}
          cy={core.y}
          r={54}
          fill="none"
          stroke="rgba(184,173,255,0.10)"
          strokeWidth={0.8}
          strokeDasharray="1.5 9"
        >
          <animateTransform
            attributeName="transform"
            attributeType="XML"
            type="rotate"
            from={`0 ${core.x} ${core.y}`}
            to={`360 ${core.x} ${core.y}`}
            dur="44s"
            repeatCount="indefinite"
          />
        </circle>

        {/* orbital ring + two orbiting dots, rotating the other way */}
        <g>
          <circle
            cx={core.x}
            cy={core.y}
            r={38}
            fill="none"
            stroke="rgba(139,123,240,0.12)"
            strokeWidth={0.8}
          />
          <circle cx={core.x + 38} cy={core.y} r={1.8} fill="#cabfff" />
          <circle cx={core.x - 38} cy={core.y} r={1.4} fill="#a99bff" opacity={0.7} />
          <animateTransform
            attributeName="transform"
            attributeType="XML"
            type="rotate"
            from={`360 ${core.x} ${core.y}`}
            to={`0 ${core.x} ${core.y}`}
            dur="56s"
            repeatCount="indefinite"
          />
        </g>

        {/* radar pulse rings (two, offset) */}
        <circle className="nc-ring" cx={core.x} cy={core.y} r={16} />
        <circle className="nc-ring" cx={core.x} cy={core.y} r={16} style={{ animationDelay: '2.2s' }} />

        {/* breathing core glow + bright center */}
        <circle
          className="nc-core-breathe"
          cx={core.x}
          cy={core.y}
          r={20}
          fill="url(#nc-core-grad)"
        />
        <circle className="nc-node" cx={core.x} cy={core.y} r={9} filter="url(#nc-glow-strong)" />
        <circle className="nc-node-dot" cx={core.x} cy={core.y} r={5} />
      </g>
    </svg>
  )
}

const AuthMarketingPanel: React.FC<AuthMarketingPanelProps> = ({ heading, subtext, bullets }) => {
  const spotRef = useRef<HTMLDivElement>(null)
  const parallaxRef = useRef<HTMLDivElement>(null)

  // Cursor move → (1) spotlight follows the mouse, (2) constellation gets a subtle two-layer
  // parallax offset via --px/--py (near layer moves fully, nebula layer moves a fraction).
  const handleMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    if (spotRef.current) {
      spotRef.current.style.setProperty('--mx', `${x}px`)
      spotRef.current.style.setProperty('--my', `${y}px`)
      spotRef.current.style.opacity = '1'
    }
    if (parallaxRef.current) {
      const nx = x / rect.width - 0.5 // -0.5 … 0.5
      const ny = y / rect.height - 0.5
      parallaxRef.current.style.setProperty('--px', `${(nx * 18).toFixed(1)}px`)
      parallaxRef.current.style.setProperty('--py', `${(ny * 12).toFixed(1)}px`)
    }
  }, [])
  const handleLeave = useCallback(() => {
    if (spotRef.current) spotRef.current.style.opacity = '0'
    if (parallaxRef.current) {
      parallaxRef.current.style.setProperty('--px', '0px')
      parallaxRef.current.style.setProperty('--py', '0px')
    }
  }, [])

  return (
    <div
      className="hidden lg:flex relative flex-col justify-between w-[60%] flex-shrink-0 min-h-screen overflow-hidden px-16 py-14 select-none"
      style={{ background: '#0c0a1e' }}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
    >
      {/* Aurora gradient-mesh blobs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute w-[620px] h-[620px] rounded-full blur-[120px] opacity-[0.5]"
          style={{ background: 'radial-gradient(circle,#5b46c9 0%,transparent 70%)', top: '-14%', right: '-16%', animation: 'aurora-a 18s ease-in-out infinite' }} />
        <div className="absolute w-[520px] h-[520px] rounded-full blur-[130px] opacity-[0.42]"
          style={{ background: 'radial-gradient(circle,#7b6ae8 0%,transparent 70%)', bottom: '-18%', left: '-14%', animation: 'aurora-b 22s ease-in-out infinite' }} />
        <div className="absolute w-[360px] h-[360px] rounded-full blur-[110px] opacity-[0.3]"
          style={{ background: 'radial-gradient(circle,#3a2a80 0%,transparent 70%)', top: '42%', left: '30%', animation: 'aurora-a 26s ease-in-out infinite' }} />
      </div>

      {/* Masked fine dot-grid texture */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.12) 1px, transparent 1px)',
          backgroundSize: '26px 26px',
          maskImage: 'radial-gradient(ellipse 80% 70% at 60% 30%, #000 0%, transparent 74%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 70% at 60% 30%, #000 0%, transparent 74%)',
          opacity: 0.5,
        }}
      />

      {/* AI network constellation — fills the blank right column, layered with a nebula + parallax */}
      <div
        ref={parallaxRef}
        className="absolute right-0 top-0 bottom-0 w-[48%] pointer-events-none z-[1]"
      >
        {/* nebula — far parallax layer */}
        <div
          className="absolute inset-0 nc-parallax-far"
        >
          <div className="absolute left-1/2 top-1/2 w-[440px] h-[440px] -translate-x-1/2 -translate-y-1/2">
            <div
              className="nc-nebula w-full h-full rounded-full"
              style={{
                background: 'radial-gradient(circle, rgba(118,92,255,0.22) 0%, transparent 70%)',
                filter: 'blur(120px)',
              }}
            />
          </div>
        </div>

        {/* graph — near parallax layer, with a slow autonomous drift */}
        <div className="absolute inset-0 nc-parallax-near flex items-center justify-center">
          <div className="nc-drift h-[92%] flex items-center">
            <NetworkConstellation />
          </div>
        </div>
      </div>

      {/* Interactive cursor spotlight */}
      <div
        ref={spotRef}
        className="absolute inset-0 pointer-events-none z-[1] transition-opacity duration-300"
        style={{
          opacity: 0,
          background:
            'radial-gradient(460px circle at var(--mx, 50%) var(--my, 40%), rgba(139,123,240,0.20), transparent 62%)',
        }}
      />

      {/* Logo — icon mark + two-line wordmark */}
      <div className="relative z-10 flex items-center gap-3.5 auth-fade" style={{ animationDelay: '0ms' }}>
        <img src={logoIcon} alt="" className="h-24 w-auto object-contain" />
        <span className="text-white font-semibold text-5xl leading-[1.05] tracking-tight">
          Smart
          <br />
          Contractor
        </span>
      </div>

      {/* Bottom content */}
      <div className="relative z-10 max-w-lg">


        <h1 className="text-white text-[2.4rem] font-bold leading-[1.06] tracking-tight mb-5 auth-fade" style={{ animationDelay: '160ms' }}>
          {heading}
        </h1>

        <p className="text-white/55 text-[15px] leading-relaxed mb-9 max-w-md auth-fade" style={{ animationDelay: '240ms' }}>
          {subtext}
        </p>

        <ul className="space-y-3.5">
          {bullets.map((b, i) => (
            <li key={b} className="flex items-start gap-3 auth-fade" style={{ animationDelay: `${320 + i * 70}ms` }}>
              <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0 shadow-[0_0_8px_2px_rgba(129,110,240,0.6)]" />
              <span className="text-white/75 text-[15px] leading-snug">{b}</span>
            </li>
          ))}
        </ul>
      </div>

      <style>{`
        @keyframes aurora-a { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(-22px,26px) scale(1.08); } }
        @keyframes aurora-b { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(24px,-18px) scale(1.1); } }
        @keyframes auth-fade-in { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .auth-fade { opacity: 0; animation: auth-fade-in 0.6s cubic-bezier(0.22,1,0.36,1) forwards; }

        /* ===== AI network constellation ===== */

        /* parallax layers (near moves fully, far/nebula moves a fraction) + autonomous drift */
        .nc-parallax-near { transform: translate(var(--px,0px), var(--py,0px)); transition: transform 0.3s ease-out; }
        .nc-parallax-far  { transform: translate(calc(var(--px,0px) * 0.45), calc(var(--py,0px) * 0.45)); transition: transform 0.5s ease-out; }
        .nc-drift { animation: nc-drift 32s ease-in-out infinite; }
        @keyframes nc-drift {
          0%,100% { transform: translate(0,0); }
          25%     { transform: translate(-5px, 7px); }
          50%     { transform: translate(6px, 4px); }
          75%     { transform: translate(-3px, -6px); }
        }

        /* nebula */
        .nc-nebula { transform-origin: center; animation: nc-nebula 18s ease-in-out infinite; }
        @keyframes nc-nebula {
          0%,100% { opacity: 0.7; transform: scale(1); }
          50%     { opacity: 1;   transform: scale(1.14); }
        }

        /* micro stars */
        .nc-star {
          fill: #cdd6ff;
          transform-box: fill-box;
          transform-origin: center;
          animation: nc-twinkle var(--dur,4s) ease-in-out infinite;
        }
        @keyframes nc-twinkle {
          0%,100% { opacity: calc(var(--o,0.4) * 0.28); transform: scale(0.7); }
          50%     { opacity: var(--o,0.4);              transform: scale(1.1); }
        }

        /* space dust — floats gently upward then fades */
        .nc-dust {
          fill: #d9d2ff;
          transform-box: fill-box;
          transform-origin: center;
          animation-name: nc-dust;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
        }
        @keyframes nc-dust {
          0%   { transform: translateY(0);              opacity: 0; }
          15%  { opacity: 0.15; }
          85%  { opacity: 0.15; }
          100% { transform: translateY(var(--drift,-16px)); opacity: 0; }
        }

        /* connection wires — very thin, gentle fade */
        .nc-wire {
          stroke: rgba(150,135,240,0.16);
          stroke-width: 0.6;
          animation: nc-wire-fade 6s ease-in-out infinite;
        }
        @keyframes nc-wire-fade { 0%,100% { opacity: 0.55; } 50% { opacity: 1; } }

        /* nodes */
        .nc-node {
          fill: rgba(139,123,240,0.8);
          transform-box: fill-box;
          transform-origin: center;
          animation: nc-pulse 4s ease-in-out infinite;
        }
        @keyframes nc-pulse {
          0%,100% { transform: scale(1);    opacity: 0.35; }
          50%     { transform: scale(1.35); opacity: 0.7; }
        }
        .nc-node-dot { fill: #f5f2ff; }

        /* core radar-pulse rings */
        .nc-ring {
          fill: none;
          stroke: rgba(170,157,245,0.5);
          stroke-width: 0.8;
          transform-box: fill-box;
          transform-origin: center;
          animation: nc-ring-pulse 4.4s ease-out infinite;
        }
        @keyframes nc-ring-pulse {
          0%   { transform: scale(0.7); opacity: 0.7; }
          70%  { opacity: 0; }
          100% { transform: scale(2.6); opacity: 0; }
        }

        /* breathing core glow */
        .nc-core-breathe {
          transform-box: fill-box;
          transform-origin: center;
          animation: nc-core-breathe 5s ease-in-out infinite;
        }
        @keyframes nc-core-breathe {
          0%,100% { transform: scale(1);    opacity: 0.85; }
          50%     { transform: scale(1.12); opacity: 1; }
        }

        @media (prefers-reduced-motion: reduce) {
          .nc-parallax-near, .nc-parallax-far, .nc-drift, .nc-nebula, .nc-star, .nc-dust,
          .nc-wire, .nc-node, .nc-ring, .nc-core-breathe { animation: none; }
        }
      `}</style>
    </div>
  )
}

export default AuthMarketingPanel
